import { Typography } from '@material-ui/core';
import { Inline, Stack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { EmptySearchLoadsIcon } from 'shared/icons/EmptySearchLoadsIcon';
import {
  createSearchCriteria,
  createSimilarSearch,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { Countdown } from 'shared/ui/Countdown';
import styled from 'styled-components';
import { trackLoadboardEvent } from '../../core/LoadboardAnalytics';
import { usePostingsPageContext } from '../../core/postings/PostingsPageContext';
import { PostingLoadDTO } from '../../data/dto/PostingLoadDTO';
import { isEmptyCriteria } from '../../data/dto/PostingsPageParamsDTO';

const Wrapper = styled.div`
  padding: 32px 16px;
`;

interface PostingDetailsUnpostedLoadProps {
  load: PostingLoadDTO;
}

export function PostingDetailsUnpostedLoad({
  load,
}: PostingDetailsUnpostedLoadProps) {
  const { params, setParams } = usePostingsPageContext();
  const countDownTime = useMemo(() => {
    return DateTime.now().plus({ seconds: 3 });
  }, []);

  function searchSimilarLoads() {
    const searchCriteria = createSearchCriteria({
      pickup_venues: [load.pickup.venue],
      delivery_venues: [load.delivery.venue],
    });
    setParams(() => ({
      current_tab: 'available',
      current_load_guid: undefined,
      utm_medium: 'Similar Loads',
      utm_content: 'Unposted Load',
      search_criteria: createSimilarSearch(searchCriteria),
      page_sort: ['pickup_distance,asc', 'posted_to_loadboard_at,desc'],
    }));
  }

  return (
    <Wrapper>
      <Stack space="small">
        <Inline space="xsmall" horizontalAlign="center">
          <EmptySearchLoadsIcon />
        </Inline>

        <Stack space="xsmall">
          <Typography variant="h3" align="center">
            Load is no longer available
          </Typography>

          <Stack space="small">
            {isEmptyCriteria(params) ? (
              <Countdown
                targetDate={countDownTime}
                onElapsed={searchSimilarLoads}
              >
                {(timeLeft) => {
                  const seconds = Math.max(
                    Math.round(timeLeft?.as('seconds') || 0),
                    1,
                  );
                  return (
                    <Typography align="center" color="textSecondary">
                      We&apos;ll show you similar loads in {seconds} seconds…
                    </Typography>
                  );
                }}
              </Countdown>
            ) : (
              <Typography align="center" color="textSecondary">
                Try searching for similar loads based on <br /> the origin and
                destination of this load.
              </Typography>
            )}

            <Button
              fullWidth={true}
              onClick={() => {
                trackLoadboardEvent({
                  name: 'SLB: Clicked Search Similar Loads Button',
                  source: 'Unposted Load',
                });
                searchSimilarLoads();
              }}
            >
              Search Similar Loads
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Wrapper>
  );
}
